import React, { ReactNode, forwardRef } from "react";
import clsx from "clsx";
import { Check, Minus } from "lucide-react";

// Base Checkbox Component
interface CheckboxProps {
  checked?: boolean;
  indeterminate?: boolean;
  onChange?: (checked: boolean) => void;
  label?: string | ReactNode;
  description?: string;
  disabled?: boolean;
  error?: string;
  helperText?: string;
  size?: "sm" | "md" | "lg";
  className?: string;
  labelClassName?: string;
  id?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      checked = false,
      indeterminate = false,
      onChange,
      label,
      description,
      disabled = false,
      error,
      helperText,
      size = "md",
      className,
      labelClassName,
      id,
      ...props
    },
    ref,
  ) => {
    const checkboxId = id || `checkbox-${Math.random().toString(36).substr(2, 9)}`;

    const sizeStyles = {
      sm: "h-4 w-4",
      md: "h-5 w-5",
      lg: "h-6 w-6",
    };

    const iconSizes = {
      sm: "w-3 h-3",
      md: "w-4 h-4",
      lg: "w-5 h-5",
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!disabled) {
        onChange?.(e.target.checked);
      }
    };

    const getCheckboxState = () => {
      if (indeterminate) return "indeterminate";
      if (checked) return "checked";
      return "unchecked";
    };

    const state = getCheckboxState();

    const checkboxClasses = clsx(
      "relative inline-flex items-center justify-center rounded border transition-all duration-200",
      sizeStyles[size],
      {
        // Unchecked state
        "bg-gray-900 border-gray-700": state === "unchecked" && !disabled && !error,
        // Checked state
        "bg-tracq-bright-blue border-tracq-bright-blue text-white":
          state === "checked" && !disabled,
        // Indeterminate state
        "bg-tracq-deep-orange border-tracq-deep-orange text-white":
          state === "indeterminate" && !disabled,
        // Disabled state
        "bg-gray-800 border-gray-700 text-gray-500 cursor-not-allowed": disabled,
        // Error state
        "border-red-500": error && !disabled,
        // Hover state
        "hover:border-tracq-bright-blue": !disabled && !error && state === "unchecked",
      },
    );

    return (
      <div className={clsx("flex flex-col", className)}>
        <div className="flex items-start gap-3">
          {/* Hidden input */}
          <input
            ref={ref}
            type="checkbox"
            id={checkboxId}
            checked={checked}
            onChange={handleChange}
            disabled={disabled}
            className="sr-only"
            {...props}
          />

          {/* Visual checkbox */}
          <label
            htmlFor={checkboxId}
            className={clsx(
              "flex items-center gap-3 cursor-pointer",
              disabled && "cursor-not-allowed",
              labelClassName,
            )}
          >
            <div className={checkboxClasses}>
              {state === "checked" && <Check className={iconSizes[size]} />}
              {state === "indeterminate" && <Minus className={iconSizes[size]} />}
            </div>

            {/* Label and description */}
            {(label || description) && (
              <div className="flex flex-col">
                {label && (
                  <span
                    className={clsx(
                      "text-sm font-medium",
                      disabled ? "text-gray-500" : "text-gray-200",
                    )}
                  >
                    {label}
                  </span>
                )}
                {description && (
                  <span
                    className={clsx("text-xs mt-1", disabled ? "text-gray-500" : "text-gray-400")}
                  >
                    {description}
                  </span>
                )}
              </div>
            )}
          </label>
        </div>

        {/* Helper/Error text */}
        {(helperText || error) && (
          <div className={clsx("mt-2 text-sm", error ? "text-red-400" : "text-gray-400")}>
            {error || helperText}
          </div>
        )}
      </div>
    );
  },
);

Checkbox.displayName = "Checkbox";

// Checkbox Group Component
interface CheckboxGroupProps {
  options: Array<{
    value: string;
    label: string;
    description?: string;
    disabled?: boolean;
  }>;
  value?: string[];
  onChange?: (value: string[]) => void;
  label?: string;
  description?: string;
  error?: string;
  helperText?: string;
  size?: "sm" | "md" | "lg";
  className?: string;
  orientation?: "vertical" | "horizontal";
}

export const CheckboxGroup: React.FC<CheckboxGroupProps> = ({
  options,
  value = [],
  onChange,
  label,
  description,
  error,
  helperText,
  size = "md",
  className,
  orientation = "vertical",
}) => {
  const handleOptionChange = (optionValue: string, checked: boolean) => {
    if (checked) {
      onChange?.([...value, optionValue]);
    } else {
      onChange?.(value.filter((v) => v !== optionValue));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      onChange?.(options.map((option) => option.value));
    } else {
      onChange?.([]);
    }
  };

  const allSelected = options.length > 0 && value.length === options.length;
  const someSelected = value.length > 0 && value.length < options.length;

  return (
    <div className={clsx("space-y-4", className)}>
      {/* Group label */}
      {(label || description) && (
        <div>
          {label && <h3 className="text-sm font-medium text-gray-200">{label}</h3>}
          {description && <p className="text-xs text-gray-400 mt-1">{description}</p>}
        </div>
      )}

      {/* Select all option */}
      {options.length > 1 && (
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected}
          onChange={handleSelectAll}
          label="Select all"
          size={size}
        />
      )}

      {/* Options */}
      <div
        className={clsx(
          "space-y-3",
          orientation === "horizontal" && "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4",
        )}
      >
        {options.map((option) => (
          <Checkbox
            key={option.value}
            checked={value.includes(option.value)}
            onChange={(checked) => handleOptionChange(option.value, checked)}
            label={option.label}
            {...(option.description !== undefined ? { description: option.description } : {})}
            {...(option.disabled !== undefined ? { disabled: option.disabled } : {})}
            size={size}
          />
        ))}
      </div>

      {/* Helper/Error text */}
      {(helperText || error) && (
        <div className={clsx("text-sm", error ? "text-red-400" : "text-gray-400")}>
          {error || helperText}
        </div>
      )}
    </div>
  );
};

// Checkbox with Icon Component
interface CheckboxWithIconProps extends Omit<CheckboxProps, "label"> {
  icon: ReactNode;
  label: string;
  description?: string;
}

export const CheckboxWithIcon: React.FC<CheckboxWithIconProps> = ({
  icon,
  label,
  description,
  ...props
}) => {
  return (
    <Checkbox
      {...props}
      label={
        <div className="flex items-center gap-3">
          <div className="text-gray-400">{icon}</div>
          <div>
            <div className="text-sm font-medium text-gray-200">{label}</div>
            {description && <div className="text-xs text-gray-400 mt-1">{description}</div>}
          </div>
        </div>
      }
    />
  );
};

// Switch-like Checkbox Component
interface SwitchCheckboxProps extends Omit<CheckboxProps, "size"> {
  size?: "sm" | "md" | "lg";
}

export const SwitchCheckbox: React.FC<SwitchCheckboxProps> = ({
  checked = false,
  onChange,
  label,
  description,
  disabled = false,
  error,
  helperText,
  size = "md",
  className,
  ...props
}) => {
  const switchId = `switch-${Math.random().toString(36).substr(2, 9)}`;

  const sizeStyles = {
    sm: "h-5 w-9",
    md: "h-6 w-11",
    lg: "h-7 w-12",
  };

  const thumbSizes = {
    sm: "h-4 w-4",
    md: "h-5 w-5",
    lg: "h-6 w-6",
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!disabled) {
      onChange?.(e.target.checked);
    }
  };

  return (
    <div className={clsx("flex flex-col", className)}>
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          id={switchId}
          checked={checked}
          onChange={handleChange}
          disabled={disabled}
          className="sr-only"
          {...props}
        />

        <label
          htmlFor={switchId}
          className={clsx(
            "flex items-center gap-3 cursor-pointer",
            disabled && "cursor-not-allowed",
          )}
        >
          {/* Switch track */}
          <div
            className={clsx(
              "relative rounded-full transition-colors duration-200",
              sizeStyles[size],
              checked ? "bg-tracq-bright-blue" : "bg-gray-700",
              error && "border border-red-500",
            )}
          >
            {/* Switch thumb */}
            <div
              className={clsx(
                "absolute top-0.5 rounded-full bg-white transition-transform duration-200",
                thumbSizes[size],
                checked
                  ? size === "sm"
                    ? "translate-x-4"
                    : size === "md"
                      ? "translate-x-5"
                      : "translate-x-5"
                  : "translate-x-0.5",
              )}
            />
          </div>

          {/* Label and description */}
          {(label || description) && (
            <div className="flex flex-col">
              {label && (
                <span
                  className={clsx(
                    "text-sm font-medium",
                    disabled ? "text-gray-500" : "text-gray-200",
                  )}
                >
                  {label}
                </span>
              )}
              {description && (
                <span
                  className={clsx("text-xs mt-1", disabled ? "text-gray-500" : "text-gray-400")}
                >
                  {description}
                </span>
              )}
            </div>
          )}
        </label>
      </div>

      {/* Helper/Error text */}
      {(helperText || error) && (
        <div className={clsx("mt-2 text-sm", error ? "text-red-400" : "text-gray-400")}>
          {error || helperText}
        </div>
      )}
    </div>
  );
};
